/* =========================================================================
   Kunst voor iedereen — theme.js (vanilla, no deps)
   Wires the foundation hooks the section files rely on:
     [data-reveal]          -> fade/translate in once on view (.is-in)
     [data-count]           -> number count-up on first view (text = target)
     [data-room-reveal]     -> sticky-scroll crossfade engine (data-room-empty)
     [data-room-layer]      -> the crossfading layers; [data-room-base] = static room
     [data-hero-parallax]   -> hero text layer drifts up on scroll
     [data-lb-trigger]      -> lightbox (fullscreen image viewer + prev/next)
     [data-tilt]            -> subtle 3D tilt on hover (data-tilt-max)
     [data-mobile-cta]      -> mobile sticky bottom CTA bar (shows after scroll)
     [data-menu-toggle]     -> toggle [data-menu] mobile drawer
     [data-form]            -> Web3Forms submit handler (reads input[name=access_key])
     [data-scroll-progress] -> scroll progress bar
     [data-accordion-item]  -> FAQ accordion (single-open, plus->X)
     [data-hero-zoom]       -> slow scroll zoom (signature helper)
     in-page #anchors       -> smooth scroll
   Re-inits on Shopify section load so the theme editor stays live.
   ========================================================================= */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var $ = function (s, c) { return [].slice.call((c || document).querySelectorAll(s)); };

  /* ---- [data-reveal] : fire once ------------------------------------- */
  function initReveal(root) {
    var els = $('[data-reveal]:not([data-reveal-bound])', root);
    if (!els.length) return;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); el.setAttribute('data-reveal-bound', ''); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var d = e.target.getAttribute('data-reveal-delay');
        if (d) e.target.style.setProperty('--reveal-delay', d + 'ms');
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
    els.forEach(function (el) { el.setAttribute('data-reveal-bound', ''); io.observe(el); });
  }

  /* ---- [data-count] : count-up on first view ------------------------- */
  function parseCount(text) {
    var m = String(text).match(/([0-9][0-9.,\s]*)/);
    if (!m) return null;
    var num = parseInt(m[1].replace(/[.,\s]/g, ''), 10);
    if (isNaN(num)) return null;
    return { target: num, prefix: text.slice(0, m.index), suffix: text.slice(m.index + m[1].length) };
  }
  function runCount(el, delay) {
    var parsed = parseCount(el.textContent.trim());
    if (!parsed || prefersReduced) return;
    var dur = 2200, start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = parsed.prefix + Math.round(eased * parsed.target) + parsed.suffix;
      if (p < 1) requestAnimationFrame(frame);
    }
    setTimeout(function () { requestAnimationFrame(frame); }, delay || 0);
  }
  function initCount(root) {
    var els = $('[data-count]:not([data-count-bound])', root);
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(function (el) { el.setAttribute('data-count-bound', ''); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var attrDelay = e.target.getAttribute('data-count-delay');
        var delay;
        if (attrDelay !== null && attrDelay !== '') {
          delay = parseInt(attrDelay, 10) || 0;
        } else {
          var sibs = $('[data-count]', e.target.parentNode);
          var i = sibs.indexOf(e.target);
          delay = (i < 0 ? 0 : i) * 180;
        }
        runCount(e.target, delay);
        io.unobserve(e.target);
      });
    }, { threshold: 0.6 });
    els.forEach(function (el) { el.setAttribute('data-count-bound', ''); io.observe(el); });
  }

  /* ---- [data-room-reveal] : sticky-scroll crossfade ------------------ */
  function initRoom(root) {
    $('[data-room-reveal]:not([data-room-bound])', root).forEach(function (room) {
      room.setAttribute('data-room-bound', '');
      var startEmpty = room.getAttribute('data-room-empty') === 'true';
      var layers = $('[data-room-layer]', room);
      var workLayers = layers.filter(function (l) { return l.getAttribute('data-room-base') === null; });
      var baseLayer = room.querySelector('[data-room-layer][data-room-base]');
      var progressBar = room.querySelector('[data-room-progress]');
      if (!workLayers.length && !baseLayer) return;

      // Reduced motion: show work statically, pin the bar, skip scroll wiring.
      if (prefersReduced) {
        if (baseLayer) baseLayer.classList.add('is-active');
        workLayers.forEach(function (l) { l.classList.add('is-active'); l.style.opacity = '1'; });
        if (progressBar) progressBar.style.width = '100%';
        return;
      }

      function update() {
        var rect = room.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var total = rect.height - vh;
        var progress = total > 0 ? Math.min(Math.max(-rect.top / total, 0), 1) : 0;
        if (progressBar) progressBar.style.width = (progress * 100).toFixed(2) + '%';
        if (baseLayer) baseLayer.classList.add('is-active');
        if (!workLayers.length) return;

        if (startEmpty) {
          // Scroll-scrubbed reveal: empty wall held until 40%, full by ~95% (matches demo).
          var n = workLayers.length;
          var idxF = progress * n;
          workLayers.forEach(function (l, i) {
            var o = Math.min(Math.max((idxF - i - 0.4) / 0.55, 0), 1);
            l.style.opacity = o.toFixed(3);
            l.classList.remove('is-active');
          });
        } else {
          var idx = Math.min(Math.floor(progress * workLayers.length), workLayers.length - 1);
          workLayers.forEach(function (l, i) { l.classList.toggle('is-active', i === idx); });
        }
      }
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
    });
  }

  /* ---- [data-hero-parallax] : hero text layer drifts up -------------- */
  function initHeroParallax(root) {
    if (prefersReduced) return;
    $('[data-hero-parallax]:not([data-hp-bound])', root).forEach(function (el) {
      el.setAttribute('data-hp-bound', '');
      function onsc() {
        var h = el.offsetHeight || 1;
        var p = Math.min(Math.max(window.scrollY / h, 0), 1);
        el.style.transform = 'translateY(' + (-10 * p) + '%)';
      }
      onsc();
      window.addEventListener('scroll', onsc, { passive: true });
      window.addEventListener('resize', onsc);
    });
  }

  /* ---- [data-tilt] : subtle 3D tilt on hover ------------------------- */
  function initTilt(root) {
    if (prefersReduced || window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
    $('[data-tilt]:not([data-tilt-bound])', root).forEach(function (el) {
      el.setAttribute('data-tilt-bound', '');
      var max = parseFloat(el.getAttribute('data-tilt-max')) || 6;
      el.style.transition = 'transform .3s cubic-bezier(.22,1,.36,1)';
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = 'perspective(1400px) rotateX(' + (-py * 2 * max).toFixed(2) + 'deg) rotateY(' + (px * 2 * max).toFixed(2) + 'deg) scale(1.02)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transform = 'perspective(1400px) rotateX(0deg) rotateY(0deg) scale(1)';
      });
    });
  }

  /* ---- [data-lb-trigger] : lightbox ---------------------------------- */
  function initLightbox() {
    var lb = null, cur = 0, triggers = [];
    function build() {
      lb = document.createElement('div');
      lb.className = 'kvi-lightbox';
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.innerHTML =
        '<button type="button" class="kvi-lightbox__btn kvi-lightbox__close" aria-label="Sluiten">&times;</button>' +
        '<button type="button" class="kvi-lightbox__btn kvi-lightbox__prev" aria-label="Vorige">&#8249;</button>' +
        '<img class="kvi-lightbox__img" alt="">' +
        '<button type="button" class="kvi-lightbox__btn kvi-lightbox__next" aria-label="Volgende">&#8250;</button>' +
        '<div class="kvi-lightbox__cap"></div>';
      document.body.appendChild(lb);
      lb.addEventListener('click', function (e) {
        if (e.target === lb || e.target.closest('.kvi-lightbox__close')) { closeLb(); }
        else if (e.target.closest('.kvi-lightbox__prev')) { show(cur - 1); }
        else if (e.target.closest('.kvi-lightbox__next')) { show(cur + 1); }
      });
    }
    function show(i) {
      triggers = $('[data-lb-trigger]');
      if (!triggers.length) return;
      cur = (i + triggers.length) % triggers.length;
      var t = triggers[cur];
      var img = lb.querySelector('.kvi-lightbox__img');
      img.src = t.getAttribute('data-lb-img') || '';
      img.alt = t.getAttribute('data-lb-cap') || '';
      lb.querySelector('.kvi-lightbox__cap').textContent = t.getAttribute('data-lb-cap') || '';
    }
    function openLb(i) {
      if (!lb) build();
      show(i);
      lb.classList.add('is-open');
      document.body.classList.add('kvi-no-scroll');
    }
    function closeLb() {
      if (!lb) return;
      lb.classList.remove('is-open');
      document.body.classList.remove('kvi-no-scroll');
    }
    document.addEventListener('click', function (e) {
      var trig = e.target.closest('[data-lb-trigger]');
      if (!trig) return;
      e.preventDefault();
      triggers = $('[data-lb-trigger]');
      openLb(triggers.indexOf(trig));
    });
    document.addEventListener('keydown', function (e) {
      var t = e.target;
      if ((e.key === 'Enter' || e.key === ' ') && t && t.getAttribute && t.getAttribute('data-lb-trigger') !== null) {
        e.preventDefault();
        triggers = $('[data-lb-trigger]');
        openLb(triggers.indexOf(t));
        return;
      }
      if (!lb || !lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLb();
      else if (e.key === 'ArrowLeft') show(cur - 1);
      else if (e.key === 'ArrowRight') show(cur + 1);
    });
  }

  /* ---- mobile drawer : [data-menu-toggle] / [data-menu] -------------- */
  function initMenu() {
    var drawer = document.querySelector('[data-menu]');
    if (!drawer) return;
    function open() { drawer.classList.add('is-open'); document.body.classList.add('kvi-no-scroll'); }
    function close() { drawer.classList.remove('is-open'); document.body.classList.remove('kvi-no-scroll'); }
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-menu-toggle]');
      if (toggle) { e.preventDefault(); drawer.classList.contains('is-open') ? close() : open(); return; }
      if (drawer.classList.contains('is-open') && e.target === drawer) close();
      if (e.target.closest('[data-menu] a')) close();
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* ---- mobile sticky CTA : [data-mobile-cta] ------------------------- */
  function initStickyCta() {
    var bar = document.querySelector('[data-mobile-cta]');
    if (!bar) return;
    function onsc() { bar.classList.toggle('is-visible', window.scrollY > 120); }
    onsc();
    window.addEventListener('scroll', onsc, { passive: true });
  }

  /* ---- scroll progress bar : [data-scroll-progress] ------------------ */
  function initProgress() {
    var wrap = document.querySelector('[data-scroll-progress]');
    if (!wrap) return;
    var bar = wrap.querySelector('.kvi-progress__bar') || wrap;
    var ticking = false;
    function set() {
      var h = document.documentElement;
      var max = (h.scrollHeight - h.clientHeight) || 1;
      bar.style.transform = 'scaleX(' + Math.min(Math.max(h.scrollTop / max, 0), 1) + ')';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(set); ticking = true; }
    }, { passive: true });
    set();
  }

  /* ---- Web3Forms submit : [data-form] -------------------------------- */
  function initForms(root) {
    $('[data-form]:not([data-form-bound])', root).forEach(function (form) {
      form.setAttribute('data-form-bound', '');
      var msg = form.querySelector('.kvi-form__msg');
      if (!msg) { msg = document.createElement('div'); msg.className = 'kvi-form__msg'; form.appendChild(msg); }
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var key = form.querySelector('input[name="access_key"]');
        if (!key || !key.value) {
          msg.className = 'kvi-form__msg is-error';
          msg.textContent = 'Formulier nog niet geconfigureerd (Web3Forms-sleutel ontbreekt).';
          return;
        }
        var btn = form.querySelector('[type="submit"]');
        var btnText = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = 'Versturen…'; }
        msg.className = 'kvi-form__msg'; msg.textContent = '';

        fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: new FormData(form)
        })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          if (json.success) {
            msg.className = 'kvi-form__msg is-success';
            msg.textContent = 'Bedankt! We hebben je bericht goed ontvangen.';
            form.reset();
          } else {
            msg.className = 'kvi-form__msg is-error';
            msg.textContent = json.message || 'Er ging iets mis. Probeer opnieuw of bel ons.';
          }
        })
        .catch(function () {
          msg.className = 'kvi-form__msg is-error';
          msg.textContent = 'Er ging iets mis. Probeer opnieuw of bel ons.';
        })
        .finally(function () { if (btn) { btn.disabled = false; btn.textContent = btnText; } });
      });
    });
  }

  /* ---- FAQ accordion : [data-accordion-item] (single-open, plus->X) -- */
  function initAccordion(root) {
    $('[data-accordion-item]:not([data-acc-bound])', root).forEach(function (item) {
      item.setAttribute('data-acc-bound', '');
      var btn = item.querySelector('[data-accordion-trigger]');
      var panel = item.querySelector('[data-accordion-panel]');
      if (!btn || !panel) return;
      var icon = btn.querySelector('span[aria-hidden]');
      panel.style.overflow = 'hidden';
      panel.style.transition = 'max-height .5s cubic-bezier(.22,1,.36,1), opacity .4s ease';
      var startOpen = item.getAttribute('data-open') === 'true';
      panel.style.maxHeight = startOpen ? panel.scrollHeight + 'px' : '0px';
      panel.style.opacity = startOpen ? '1' : '0';
      if (startOpen && icon) icon.classList.add('rotate-45');
      btn.addEventListener('click', function () {
        var open = item.getAttribute('data-open') === 'true';
        if (!open) {
          var list = item.closest('ul') || item.parentNode;
          $('[data-accordion-item]', list).forEach(function (other) {
            if (other === item) return;
            other.setAttribute('data-open', 'false');
            var ot = other.querySelector('[data-accordion-trigger]');
            if (ot) ot.setAttribute('aria-expanded', 'false');
            var op = other.querySelector('[data-accordion-panel]');
            if (op) { op.style.maxHeight = '0px'; op.style.opacity = '0'; }
            var oi = ot && ot.querySelector('span[aria-hidden]');
            if (oi) oi.classList.remove('rotate-45');
          });
        }
        item.setAttribute('data-open', open ? 'false' : 'true');
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.style.maxHeight = open ? '0px' : panel.scrollHeight + 'px';
        panel.style.opacity = open ? '0' : '1';
        if (icon) icon.classList.toggle('rotate-45', !open);
      });
    });
  }

  /* ---- hero zoom : [data-hero-zoom] (signature helper) --------------- */
  function initHeroZoom(root) {
    $('[data-hero-zoom]:not([data-zoom-bound])', root).forEach(function (img) {
      img.setAttribute('data-zoom-bound', '');
      if (prefersReduced) return;
      function onsc() { img.style.transform = 'scale(' + (1 + Math.min(window.scrollY / 1600, 0.09)) + ')'; }
      onsc();
      window.addEventListener('scroll', onsc, { passive: true });
    });
  }

  /* ---- smooth scroll for in-page #anchors ---------------------------- */
  function initAnchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"], a[href*="/#"]');
      if (!a) return;
      var href = a.getAttribute('href');
      var hashIndex = href.indexOf('#');
      if (hashIndex === -1) return;
      var id = href.slice(hashIndex + 1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      var path = href.slice(0, hashIndex);
      if (path && path !== window.location.pathname && path !== '/') return;
      e.preventDefault();
      target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
      history.pushState(null, '', '#' + id);
    });
  }

  /* ---- boot ---------------------------------------------------------- */
  function boot(root) {
    initReveal(root);
    initCount(root);
    initRoom(root);
    initForms(root);
    initAccordion(root);
    initHeroZoom(root);
    initHeroParallax(root);
    initTilt(root);
  }
  function bootGlobal() {
    initMenu();
    initProgress();
    initAnchors();
    initLightbox();
    initStickyCta();
    boot(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootGlobal);
  } else {
    bootGlobal();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });
})();
